import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search } from "lucide-react";
import { COUNTRY_DIAL_CODES, COUNTRY_NAMES_HE } from "@multi-chat/shared";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover/index.js";
import { cn } from "../../lib/utils.js";
import { contactsViewportPopoverClass } from "../contacts/contacts-table-presentation.js";
import {
  campaignCountryCompactText,
  campaignCountryDisplayText,
} from "./campaigns-presentation.js";

export function CampaignCountrySelect({
  value,
  onChange,
  disabled,
  id,
  compact = false,
}: {
  value: string;
  onChange: (iso: string) => void;
  disabled?: boolean;
  id?: string;
  compact?: boolean;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const isHebrewUi = i18n.language === "he";
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected =
    COUNTRY_DIAL_CODES.find((country) => country.code === value) ??
    COUNTRY_DIAL_CODES.find((country) => country.code === "IL");

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return COUNTRY_DIAL_CODES;
    return COUNTRY_DIAL_CODES.filter((country) => {
      const hebrewName = COUNTRY_NAMES_HE[country.code] ?? "";
      return (
        country.name.toLowerCase().includes(query) ||
        hebrewName.toLowerCase().includes(query) ||
        country.dialCode.toLowerCase().includes(query) ||
        country.code.toLowerCase().includes(query)
      );
    });
  }, [search]);

  const selectedLabel = selected
    ? isHebrewUi
      ? (COUNTRY_NAMES_HE[selected.code] ?? selected.name)
      : selected.name
    : value;
  const selectedText = selected
    ? compact
      ? campaignCountryCompactText(selected)
      : campaignCountryDisplayText(selected, selectedLabel)
    : value;

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-2.5 text-start text-[0.8125rem] disabled:opacity-70"
          data-testid="campaign-country-selector"
          aria-label={selectedText}
        >
          <span className={compact ? "whitespace-nowrap" : "min-w-0 truncate"}>
            {selectedText}
          </span>
          <ChevronDown size={14} className="shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(22rem,calc(100vw-2rem))] overflow-hidden p-0">
        <div className="flex items-center gap-2 border-b border-line px-2.5 py-2">
          <Search size={14} className="shrink-0 text-muted" aria-hidden="true" />
          <input
            className="min-w-0 flex-1 border-0 bg-transparent text-[0.8125rem] outline-none"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("phoneField.countrySearch")}
          />
        </div>
        <div className={cn(contactsViewportPopoverClass, "p-1")}>
          {filtered.map((country) => {
            const localizedName = isHebrewUi
              ? (COUNTRY_NAMES_HE[country.code] ?? country.name)
              : country.name;
            const optionText = campaignCountryDisplayText(country, localizedName);
            return (
              <button
                key={country.code}
                type="button"
                className={cn(
                  "flex w-full min-w-0 items-center rounded-md px-2 py-1.5 text-start hover:bg-surface-muted",
                  country.code === selected?.code ? "bg-surface-muted" : null,
                )}
                onClick={() => {
                  onChange(country.code);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <span className="min-w-0 truncate text-[0.8125rem]">{optionText}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
